import { getIdToken } from './auth-provider';
import { apiErrorMessage } from '../lib/api-error';

export class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

/**
 * Authenticated fetch for admin API calls.
 *
 * Why this exists: page-level handlers were reading the bearer token from
 * `localStorage.getItem('access_token')` — a key the auth-provider never writes
 * (tokens live inside the in-memory amazon-cognito-identity-js session). The
 * request went out as `Authorization: Bearer null`, API Gateway's Cognito
 * Authorizer denied, and admins saw an opaque 401 on delete/suspend/etc. See
 * incident 2026-05-22.
 *
 * Single chokepoint properties:
 *   - Fail-closed on missing session: throws AdminApiError(401) before any
 *     network call, so a stale tab never sends `Bearer null` again.
 *   - Auto-injects `Content-Type: application/json` when a body is present.
 *   - Throws AdminApiError on non-2xx with the parsed body attached, so
 *     callers don't repeat the `if (!response.ok) { ... }` boilerplate.
 *   - Returns the raw Response on success so callers choose .json() / .blob()
 *     (waitlist export needs the blob path).
 *
 * Mirrors providers/data-provider.ts::authenticatedFetch — keep them in sync.
 */
export async function adminFetch(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = await getIdToken();
  if (!token) {
    throw new AdminApiError(401, 'No active Cognito session — please log in again.');
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, { ...init, headers });

  if (!response.ok) {
    let body: unknown;
    let message = `HTTP ${response.status}`;
    try {
      body = await response.clone().json();
      message = apiErrorMessage(body, response.status);
    } catch {
      try {
        body = await response.clone().text();
      } catch {
        body = undefined;
      }
    }
    throw new AdminApiError(response.status, message, body);
  }

  return response;
}
